/**
 * An image the user pasted into the AI assistant's composer, as the chat procedure takes it.
 *
 * Browser-safe (no server imports): the client builds it and the server bounds it with the same
 * limits. Both read the type from the image's bytes with `chatImageTypeOf`: the client to label
 * what it sends, the server to check the bytes against `mediaType`.
 */
import { z } from 'zod';
import {
    AI_CHAT_IMAGE_MEDIA_TYPES,
    AI_CHAT_MAX_IMAGE_BASE64_CHARS,
    type AiChatImageMediaType,
} from '../constants/business/ai-chat';

export const chatImageSchema = z.strictObject({
    mediaType: z.enum(AI_CHAT_IMAGE_MEDIA_TYPES),
    /** The image file's bytes, base64-encoded without a `data:` prefix. */
    data: z.base64().min(1).max(AI_CHAT_MAX_IMAGE_BASE64_CHARS),
});

export type ChatImage = z.infer<typeof chatImageSchema>;

/** How many leading bytes `chatImageTypeOf` reads. */
export const CHAT_IMAGE_SIGNATURE_BYTES = 12;

const startsWith = (bytes: Uint8Array, signature: readonly number[], offset = 0) =>
    signature.every((byte, index) => bytes[offset + index] === byte);

const ascii = (text: string) => Array.from(text, (char) => char.charCodeAt(0));

/** Whether `bytes` start with the file signature of each accepted type. */
const HAS_SIGNATURE: Record<AiChatImageMediaType, (bytes: Uint8Array) => boolean> = {
    'image/png': (bytes) => startsWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
    'image/jpeg': (bytes) => startsWith(bytes, [0xff, 0xd8, 0xff]),
    'image/webp': (bytes) => startsWith(bytes, ascii('RIFF')) && startsWith(bytes, ascii('WEBP'), 8),
};

/** The accepted type whose file signature `bytes` start with, or undefined for any other file. */
export function chatImageTypeOf(bytes: Uint8Array): AiChatImageMediaType | undefined {
    return AI_CHAT_IMAGE_MEDIA_TYPES.find((mediaType) => HAS_SIGNATURE[mediaType](bytes));
}
